import useAxios from 'axios-hooks';
import { Button, LoadPanel, Template } from 'devextreme-react';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import React, {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
  useMemo,
} from 'react';
import Form from 'devextreme-react/form';
import { useMallLinkedTargetSetting } from '..';
import { defaultValue } from './DataConst';
import { notification } from 'utils/notification';
import PopupConfirm from 'app/components/PopupCommon/PopupConfirm';
import HyperLink from 'app/components/Templates/HyperLink';
import FormSearch from './FormSearch';
import PopupSelectCommonCode from 'app/components/PopupCommon/PopupSelectCommonCode';
import { cloneDeep } from 'lodash';
import { DataGridFull } from 'app/components/DataGrid';
import useFormat from 'hooks/useFormat';
import { CustomRule } from 'devextreme/ui/validation_rules';

const CreateContext = createContext({});

export const useCreate = () => useContext(CreateContext);

const ViewEditCreate = () => {
  const rootContext: any = useMallLinkedTargetSetting();
  const { modeView, setModeView, setData, t } = rootContext;
  const { QtyFormat, AmountFormat, DoubleFormat } = useFormat();
  const formRef: any = useRef(null);
  const [mallInfo, setMallInfo] = useState<any>({ ...defaultValue });
  const [showPopupConfirmSave, setShowPopupConfirmSave] = useState(false);
  const [showPopupConfirmCancel, setShowPopupConfirmCancel] = useState(false);
  const [productInfo, setProductInfo] = useState<any>();
  const [productResult, setProductResult] = useState<any>(null);
  const [showPopupByType, setShowPopupByType] = useState('');
  const [productAuthTpState, setProductAuthTpState] = useState<{
    [key: number]: string;
  }>({});
  const [mallAuthTpState, setMallAuthTpState] = useState<{
    [key: number]: string;
  }>({});
  const [productGridRows, setProductGridRows] = useState<any[]>([]);

  const [{ loading: loadingProduct }, refetchData] = useAxios(
    {
      url: '/product/product-master-inquiry',
      method: 'POST',
    },
    { manual: true },
  );

  const [{ loading: loadingSave }, refetchDataOther] = useAxios(
    {},
    { manual: true },
  );

  const [{ data: resData, loading: loadingFetch }, refetchMallInfo] = useAxios(
    {
      url: `/ecommerce/m1046_b/${modeView.mallInfoId}`,
      method: 'GET',
    },
    {
      manual: true,
      autoCancel: true,
      useCache: false,
    },
  );

  useEffect(() => {
    if (modeView?.mallInfoId) {
      refetchMallInfo();
    }
  }, [modeView?.mallInfoId]);

  // 왼쪽 그리드: 빈칸 포함 순환
  const toggleAuthTpWithBlank = (value: string = ''): string => {
    switch (value) {
      case '+':
        return '-';
      case '-':
        return '';
      default:
        return '+';
    }
  };

  // 오른쪽 그리드: 빈칸 제외, + ↔ - 반복
  const toggleAuthTpNoBlank = (value: string = ''): string => {
    return value === '+' ? '-' : '+';
  };

  useEffect(() => {
    const data = resData?.data;
    if (!data) return;

    const isArray = Array.isArray(data);
    const first = isArray ? data[0] : data;
    if (!first) return;

    const productTpArray =
      first.productTp?.split(',').map((item: string) => item.trim()) || [];
    const linkagetargetArray =
      first.linkagetarget?.split(',').map((item: string) => item.trim()) || [];

    setMallInfo({
      mallInfoId: first.mallInfoId || '',
      mallCd: first.mallCd || '',
      mallNm: first.mallNm || '',
      productTp: productTpArray,
      linkagetarget: productTpArray,
      products: isArray
        ? data
            .filter((item: any) => !!item.productId)
            .map((item: any) => ({
              productId: item.productId,
              authTp: item.authTp,
              productCd: item.productCd,
              productNm: item.productNm,
              marketableSize: item.marketableSize,
            }))
        : first.products || [],
      dataList: {
        linkagetarget: productTpArray.map((code, idx) => ({
          code,
          name: linkagetargetArray[idx] || code,
        })),
      },
    });
  }, [resData]);

  const onSubmit = () => {
    const isValid = formRef?.current?.instance?.validate()?.isValid;
    if (!isValid) return;
    setShowPopupConfirmSave(true);
  };

  const onSave = async () => {
    if (!mallInfo.mallInfoId || !Array.isArray(mallInfo.products)) return;
    const productTypes =
      mallInfo.dataList?.linkagetarget?.map(item => item.code) || [];

    const requestData = {
      mallInfoId: mallInfo.mallInfoId,
      productTypes,
      productIds: mallInfo.products?.map(p => p.productId),
      authTypes: mallInfo.products?.map((_, idx) => mallAuthTpState[idx] || ''),
    };

    try {
      const res = await refetchDataOther({
        url: '/ecommerce/m1046_b',
        method: 'PUT',
        data: requestData,
      });

      if (!res) {
        notification({
          type: 'error',
          message: t('No response received from server'),
        });
        setShowPopupConfirmSave(false);
        return;
      }

      if (res.data?.status !== '201') {
        notification({
          type: 'error',
          message:
            t('Save failed') +
            `: ${res.data?.message || t('Unexpected error')}`,
        });
        setShowPopupConfirmSave(false);
        return;
      }

      const updatedData = res.data?.data || {};
      updatedData.useYn = updatedData.useYn === 'Y';
      updatedData.defaultYn = updatedData.defaultYn === 'Y';
      updatedData.openMarketYn = updatedData.openMarketYn === 'Y';

      const linkagetargetArray = updatedData.linkagetarget?.split(',') || [];

      const productTypesForDisplay = linkagetargetArray.map(code => ({
        code,
        name: code,
      }));

      setMallInfo(prev => ({
        ...prev,
        ...updatedData,
        productTp: updatedData.productTp?.split(',') || [],
        linkagetarget: linkagetargetArray,
        dataList: {
          ...(prev.dataList || {}),
          linkagetarget: productTypesForDisplay,
        },
      }));

      setData(prev => {
        const updated = [...prev];
        const idx = updated.findIndex(
          item => item.mallInfoId === updatedData.mallInfoId,
        );
        if (idx !== -1) updated.splice(idx, 1, updatedData);
        else updated.unshift(updatedData);
        return updated.filter(item => !!item.mallInfoId);
      });

      notification({ type: 'success', message: t('Saved successfully') });
      setModeView({ type: 'list', mallInfoId: '' });
    } catch (error: any) {
      console.error('Save error:', error);
      notification({
        type: 'error',
        message: error?.message || t('An error occurred while saving'),
      });
      setShowPopupConfirmSave(false);
    }
  };

  const removeStore = (fieldName: string, index: number) => {
    const list = cloneDeep(mallInfo.dataList || {});
    if (index === -1) list[fieldName] = [];
    else list[fieldName]?.splice(index, 1);

    setMallInfo({ ...mallInfo, dataList: list });
  };

  const updateStore = (fieldName: string, items: any[]) => {
    const newList = items.map(({ code, name }) => ({ code, name }));
    const updated = cloneDeep(mallInfo);

    updated.dataList = {
      ...updated.dataList,
      [fieldName]: newList,
    };

    updated[fieldName] = newList.map(i => i.code);

    setMallInfo(updated);
    setShowPopupByType('');
  };

  const generateDxTextBox = (
    fieldLabel: string,
    fieldName: string,
    fieldBtnText: string,
    numberShowMore = 3,
  ) => {
    const list = mallInfo?.dataList?.[fieldName] || [];
    const buttons = list.slice(0, numberShowMore).map((data, index) => ({
      name: `${fieldName}${index}`,
      location: 'before' as const,
      options: {
        icon: 'close',
        text: data.name,
        onClick: () => removeStore(fieldName, index),
      },
    }));

    if (list.length > numberShowMore) {
      buttons.push({
        name: `${fieldName}More`,
        location: 'before' as const,
        options: {
          text: `+ ${list.length - numberShowMore} ${t('more')}`,
          onClick: () => setShowPopupByType(fieldName),
        },
      });
    }

    buttons.push({
      name: 'search',
      location: 'after' as const,
      options: {
        stylingMode: 'text',
        icon: 'search',
        onClick: () => {
          setMallInfo(prev => ({ ...prev, [fieldName]: '' }));
          setShowPopupByType(fieldName);
        },
      },
    });

    return {
      label: { text: t(fieldLabel) },
      dataField: fieldName,
      editorType: 'dxTextBox' as 'dxTextBox',
      validationRules: [
        {
          type: 'custom',
          message: t('Please select at least one product type'),
          validationCallback: () => mallInfo?.dataList?.[fieldName]?.length > 0,
        } as CustomRule,
      ],
      editorOptions: {
        value: '',
        onEnterKey: () => setShowPopupByType(fieldName),
        showClearButton: true,
        buttons,
        width: Math.min(500 + list.length * 50, 1000),
      },
    };
  };

  const getPopupByType = (popupType: string) => {
    switch (popupType) {
      case 'linkagetarget':
        return (
          <PopupSelectCommonCode
            text={mallInfo?.[popupType]}
            mode="multiple"
            codeType="PT"
            visible={showPopupByType === popupType}
            onHiding={() => setShowPopupByType('')}
            onSubmit={items => updateStore(popupType, items)}
          />
        );
      default:
        return null;
    }
  };

  const handleLeftAuthTpClick = (index: number) => {
    setProductAuthTpState(prev => {
      const current = prev[index] || '';
      const next = toggleAuthTpWithBlank(current); // '', '+' → '-', '-' → ''
      return { ...prev, [index]: next };
    });
  };

  const handleDeleteProductRow = async (productIdToDelete: number) => {
    const updatedProducts = mallInfo.products?.filter(
      p => p.productId !== productIdToDelete,
    );

    // 상태 업데이트로 UI에서 row 제거
    setMallInfo(prev => ({
      ...prev,
      products: updatedProducts,
    }));

    // 백엔드 삭제 API 호출
    await refetchDataOther({
      url: '/ecommerce/m1046_b',
      method: 'DELETE',
      data: {
        mallInfoId: mallInfo.mallInfoId,
        productIds: [productIdToDelete],
      },
    });
  };

  const contextValue = {
    productInfo,
    setProductInfo,
    refetchData,
    t,
    productResult,
    setProductResult,
  };

  const columnsLeft: any = [
    {
      dataField: 'authTp',
      caption: t('Auth Type'),
      cellRender: (e: any) => {
        const index = e.rowIndex;
        const value = productAuthTpState[index] || '';
        return (
          <div
            onClick={() => handleLeftAuthTpClick(index)}
            style={{
              cursor: 'pointer',
              display: 'inline-block',
              minWidth: '100%',
              height: '100%',
              lineHeight: '24px',
              textAlign: 'center',
              border: '1px solid #ccc',
              borderRadius: 4,
              background: '#f9f9f9',
              userSelect: 'none',
              fontSize: '25px',
              fontWeight: 'bold',
            }}
          >
            {value || '\u00A0'}
          </div>
        );
      },
    },
    {
      dataField: 'product_id',
      caption: t('Product ID'),
      showWhenGrouped: true,
      visible: false,
      fixed: true,
    },
    {
      dataField: 'product_tp_nm',
      caption: t('Product Type'),
      showWhenGrouped: true,
      visible: false,
    },
    {
      dataField: 'product_option_img',
      caption: t('Product Image'),
      alignment: 'center',
      visible: false,
      cellRender: record => {
        const { product_option_img } = record?.data || {};
        return product_option_img ? (
          <div className="product-image-grid-custom">
            <img src={product_option_img} width={26} height={26} alt="" />
          </div>
        ) : null;
      },
    },
    {
      dataField: 'product_cd',
      caption: t('Product Code'),
      alignment: 'center',
      showWhenGrouped: true,
    },
    {
      dataField: 'product_nm',
      caption: t('Product'),
      showWhenGrouped: true,
    },
    {
      dataField: 'marketable_size',
      caption: t('Marketable Size'),
      showWhenGrouped: true,
    },
    {
      dataField: 'maker',
      caption: t('Maker'),
      showWhenGrouped: true,
      visible: false,
    },
    {
      dataField: 'option_cd',
      caption: t('Option Code'),
      alignment: 'center',
      visible: false,
      showWhenGrouped: true,
    },
    {
      dataField: 'option_nm',
      caption: t('Option'),
      showWhenGrouped: true,
      visible: false,
    },
    {
      dataField: 'quantity_per_pack',
      caption: t('Quantity Per Pack'),
      dataType: 'number',
      format: QtyFormat,
      showWhenGrouped: true,
    },
    {
      dataField: 'supply_unit_price',
      caption: t('Supply Unit Price'),
      dataType: 'number',
      format: AmountFormat,
      showWhenGrouped: true,
      visible: false,
    },
  ];

  const columnsRight = [
    {
      dataField: 'authTp',
      caption: t('Auth Type'),
      cellRender: (e: any) => {
        const index = e.rowIndex;
        const originalValue = e.data.authTp || ''; // 원본 값
        const toggledValue = mallAuthTpState[index]; // 클릭 상태값

        const displayValue =
          toggledValue !== undefined ? toggledValue : originalValue;
        const value = mallAuthTpState[index] ?? e.data.authTp ?? '';

        return (
          <div
            onClick={() => {
              setMallAuthTpState(prev => {
                const current = prev[index] ?? value;
                const next = toggleAuthTpNoBlank(current);
                return { ...prev, [index]: next };
              });
            }}
            style={{
              cursor: 'pointer',
              minWidth: '100%',
              height: '100%',
              lineHeight: '24px',
              textAlign: 'center',
              border: '1px solid #ccc',
              borderRadius: 4,
              background: '#f9f9f9',
              userSelect: 'none',
              fontSize: '25px',
              fontWeight: 'bold',
            }}
          >
            {value || '\u00A0'}
          </div>
        );
      },
    },
    { dataField: 'productCd', caption: t('Product Code') },
    { dataField: 'productNm', caption: t('Product Name') },
    { dataField: 'marketableSize', caption: t('Marketable Size') },
    {
      dataField: 'action',
      caption: t('Action'),
      cellRender: (e: any) => {
        const { productId } = e.data;
        return (
          <Button
            text={t('Delete')}
            icon="trash"
            type="danger"
            onClick={() => handleDeleteProductRow(productId)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (Array.isArray(productResult?.data)) {
      const existingIds = new Set(mallInfo.products?.map(p => p.productId)); // 오른쪽 그리드의 상품 ID 모음

      const mapped = productResult.data
        .filter(item => !existingIds.has(item.product_id)) // 이미 등록된 상품 제외
        .map((item, index) => ({
          ...item,
          authTp: productAuthTpState[index] || '',
        }));

      setProductGridRows(mapped);
    }
  }, [productResult, productAuthTpState, mallInfo.products]);

  useEffect(() => {
    const newState: { [key: number]: string } = {};
    mallInfo.products?.forEach((item, index) => {
      newState[index] = item.authTp || '';
    });
    setMallAuthTpState(newState);
  }, [mallInfo]);

  const applyProducts = () => {
    const selected = productResult?.data
      ?.map((item: any, idx: number) => ({
        authTp: productAuthTpState[idx],
        productId: item.product_id,
        productCd: item.product_cd,
        productNm: item.product_nm,
        marketableSize: item.marketable_size,
      }))
      ?.filter(item => item.authTp === '+' || item.authTp === '-');

    if (!selected?.length) return;

    const updatedProducts = [...(mallInfo.products || [])];
    const existingCodeIndexMap = new Map(
      updatedProducts.map((p, idx) => [p.productCd, idx]),
    );

    selected.forEach(sel => {
      const idx = existingCodeIndexMap.get(sel.productCd);
      if (idx !== undefined) {
        updatedProducts[idx].authTp = sel.authTp;
      } else {
        updatedProducts.push(sel);
      }
    });

    setMallInfo(prev => ({ ...prev, products: updatedProducts }));

    //  왼쪽 그리드에서 적용된 상품 제거
    setProductGridRows(prev =>
      prev.filter(
        row => !selected.some(sel => sel.productId === row.product_id),
      ),
    );

    setProductAuthTpState({});
  };

  return (
    <React.Suspense fallback={<LoadPanel visible />}>
      <LoadPanel visible={loadingSave || loadingFetch || loadingProduct} />

      <div
        className="title-page-wrap"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <div className="title-page">
          {t('A Mall-linked information management')}
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <Button
            text={t('Save')}
            type="default"
            icon="check"
            onClick={onSubmit}
            style={{ marginRight: 10 }}
            width={100}
          />
          <Button
            text={t('Cancel')}
            icon="close"
            onClick={() => setShowPopupConfirmCancel(true)}
            width={100}
          />
        </div>
      </div>

      <CreateContext.Provider value={contextValue}>
        <Form
          ref={formRef}
          formData={mallInfo}
          colCount={3}
          labelLocation="top"
          showColonAfterLabel={false}
          scrollingEnabled
          items={[
            {
              itemType: 'group',
              caption: t('Mall Information'),
              cssClass: 'body-padding-white mb-10',
              colSpan: 3,
              colCount: 3,
              items: [
                {
                  label: { text: t('ID') },
                  dataField: 'mallInfoId',
                  editorOptions: { disabled: true },
                },
                {
                  label: { text: t('Mall code') },
                  dataField: 'mallCd',
                  editorOptions: { disabled: true },
                },
                {
                  label: { text: t('Mall name') },
                  dataField: 'mallNm',
                  editorOptions: { disabled: true },
                },
                generateDxTextBox(
                  `${t('Product Type')} *`,
                  'linkagetarget',
                  'name',
                ),
              ],
            },
          ]}
        >
          <Template name="HyperLinkTemp" component={HyperLink} />
          <Template
            name="lineTemplate"
            component={() => (
              <div
                style={{
                  width: '100%',
                  borderTop: '2px dashed #cbcbcb',
                  height: '1px',
                  margin: '10px 0',
                }}
              />
            )}
          />
        </Form>

        <div className="body-padding-white" style={{ marginBottom: 30 }}>
          <FormSearch />
        </div>
      </CreateContext.Provider>
      <div
        className="body-padding-white"
        style={{ display: 'flex', gap: 20, marginBottom: 30 }}
      >
        {/* 왼쪽: 검색 결과 */}
        <div style={{ flex: 1 }}>
          <h4>{t('Search Results')}</h4>
          <DataGridFull
            columns={columnsLeft}
            dataSource={productGridRows}
            options={{
              groupPanel: {
                visible: true,
              },
              columnAutoWidth: true,
              scrolling: {
                columnRenderingMode: 'virtual',
              },
              onToolbarPreparing: (e: any) => {
                e.toolbarOptions.items.unshift({
                  location: 'before',
                  template: 'totalCount',
                });
              },
              summary: {
                totalItems: [{}],
                groupItems: [
                  {
                    summaryType: 'count',
                    displayFormat: '{0}',
                  },
                ],
              },
            }}
          />
        </div>
        {/* 가운데 Apply 버튼 */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            paddingTop: 45,
          }}
        >
          <Button
            text={t('Apply')}
            icon="arrowright"
            type="default"
            onClick={applyProducts}
            width={80}
            height={100}
          />
        </div>

        {/* 오른쪽: Mall 정보에서 가져온 상품 데이터 (리스트 형태) */}
        <div style={{ flex: 1 }}>
          <h4>{t('Mall Product Info')}</h4>
          <DataGridFull
            columns={columnsRight}
            dataSource={mallInfo.products || []}
            options={{
              groupPanel: {
                visible: true,
              },
              columnAutoWidth: true,
              scrolling: {
                columnRenderingMode: 'virtual',
              },
              onToolbarPreparing: (e: any) => {
                e.toolbarOptions.items.unshift({
                  location: 'before',
                  template: 'totalCount',
                });
              },
              summary: {
                totalItems: [{}],
                groupItems: [
                  {
                    summaryType: 'count',
                    displayFormat: '{0}',
                  },
                ],
              },
            }}
          />
        </div>
      </div>

      <PopupConfirm
        visible={showPopupConfirmSave}
        content={t('Are you sure to save?')}
        onOk={onSave}
        onHiding={() => setShowPopupConfirmSave(false)}
      />
      <PopupConfirm
        visible={showPopupConfirmCancel}
        content={t('Are you sure to cancel all the change?')}
        onOk={() => setModeView({ type: 'list', mallInfoId: '' })}
        onHiding={() => setShowPopupConfirmCancel(false)}
      />
      {getPopupByType(showPopupByType)}
    </React.Suspense>
  );
};

export default ViewEditCreate;
